import { Injectable } from '@angular/core';

import { Placeholder } from '../../../wysiwyg-extensions/placeholders/placeholder';
import { Redactor, RedactorApp, RedactorComponentType, RedactorConfig } from '../../redactor.types';
import { RedactorPlugin, Wrapper } from '../interfaces';
import { ANGULAR_ADAPTER_PLUGIN_TOKEN } from '../redactor-adapter';
import { mergeRedactorConfigArrField, RedactorExtension } from '../redactor-extension';

declare let $R: Redactor;

// plugin constants
export const PLACEHOLDER_PLUGIN_TOKEN = 'rt-placeholder';
export const PLACEHOLDER_ANGULAR_COMPONENT = 'rt-wysiwyg-placeholder';

// redactor stuff

export class RtRedactorPlaceholder implements RedactorPlugin {
  private readonly placeholderRangeRegexp = /(=")?(\[[a-z|_.]{3,30}])/gm;
  private placeholdersMap: Record<string, Placeholder>;

  private app: RedactorApp;
  private keycodes: any;
  private selection: any;
  private caret: any;
  private inspector: any;
  private utils: any;
  private insertion: any;
  private component: any;
  private opts: any;
  private cleaner: any;

  public init(app: RedactorApp): void {
    this.app = app;
    this.cleaner = app.cleaner;
    this.opts = app.opts;
    this.component = app.component;
    this.utils = app.utils;
    this.insertion = app.insertion;
    this.inspector = app.inspector;
    this.caret = app.caret;
    this.selection = app.selection;
    this.keycodes = app.keycodes;
    this.placeholdersMap = {};
  }

  // eslint-disable-next-line max-lines-per-function
  public start(): void {
    this.updatePlaceholdersMap();

    this.cleaner.addConvertRules(PLACEHOLDER_PLUGIN_TOKEN, ($wrapper: Wrapper) => {
      if (!this.opts.rtPlaceholders || this.opts.rtPlaceholders.length === 0) return;
      let $$wrapper = $wrapper;
      // console.time('placeholder_convert');

      const html = $$wrapper.html();
      const convertedHtml = html.replace(this.placeholderRangeRegexp, match => {
        if (match.startsWith('="')) return match;

        const placeholder = this.placeholdersMap[match];
        return placeholder ? this.createComponent(placeholder).get().outerHTML : match;
      });

      $$wrapper = $$wrapper.html(convertedHtml);

      if (this.opts.rtPlaceholdersExcludeParentSelector) {
        $$wrapper = $$wrapper
          .find(
            `${this.opts.rtPlaceholdersExcludeParentSelector} [data-angular-type="${PLACEHOLDER_ANGULAR_COMPONENT}"]`,
          )
          .replaceWith(node =>
            document.createTextNode(decodeURI(node.dataset['interpolatedValue'])),
          );
      }

      // console.timeEnd('placeholder_convert');
      return $$wrapper;
    });
    //
    this.cleaner.addUnconvertRules(PLACEHOLDER_PLUGIN_TOKEN, ($wrapper: Wrapper) => {
      // console.time('placeholder_unconvert');

      $wrapper
        .find(`[data-angular-type="${PLACEHOLDER_ANGULAR_COMPONENT}"]`)
        .replaceWith(node => document.createTextNode(decodeURI(node.dataset['interpolatedValue'])));

      // console.timeEnd('placeholder_unconvert');
      return $wrapper;
    });
  }

  public stop(): void {
    delete this.cleaner.convertRules[PLACEHOLDER_PLUGIN_TOKEN];
    delete this.cleaner.unconvertRules[PLACEHOLDER_PLUGIN_TOKEN];
  }

  public onkeydown(): void {
    const TEXT_NODE_TYPE = 3;
    const current: Node = this.selection.getCurrent();
    const current$ = $R.dom(current);

    if (!current || current.nodeType !== TEXT_NODE_TYPE) return;

    const content = current.textContent!;
    const matches = content.match(/(\[.*?\])/g);

    if (!matches) return;

    matches.forEach(match => {
      const placeholder = this.placeholdersMap[match];
      if (!placeholder) return;

      const component = this.createComponent(placeholder);
      const node: HTMLElement = component.get();
      const nodeId = node.children[0].id;
      const updatedHtml = content.replace(match, node.outerHTML + '&nbsp;');
      current$.replaceWith(updatedHtml);
      const domNode = document.getElementById(nodeId)!;
      this.caret.setAfter(domNode.parentNode);
    });
  }

  private insert(placeholder: Placeholder, restoreSelection: boolean = true): void {
    if (restoreSelection) {
      this.selection.restore();
    }

    const $cmp = this.createComponent(placeholder);
    this.insertion.insertNode($cmp);
    return $cmp;
  }

  private updatePlaceholdersData(placeholders: Placeholder[]): void {
    this.opts.rtPlaceholders = placeholders;
    this.updatePlaceholdersMap();
  }

  private createComponent(placeholder: Placeholder): RedactorComponentType {
    const cmp$ = this.component.create(ANGULAR_ADAPTER_PLUGIN_TOKEN, {
      angularTagName: PLACEHOLDER_ANGULAR_COMPONENT,
      data: {
        active: placeholder,
        placeholders: this.opts.rtPlaceholders,
      },
    });

    cmp$.writeValue(placeholder.id);

    return cmp$;
  }

  private updatePlaceholdersMap(): void {
    const placeholders: Placeholder[] = this.opts.rtPlaceholders || [];
    this.placeholdersMap = placeholders.reduce((acc, p) => {
      acc[p.toString()] = p;
      return acc;
    }, <Record<string, Placeholder>>{});
  }
}

@Injectable({
  providedIn: 'root',
})
export class RedactorPlaceholderExtension extends RedactorExtension {
  constructor() {
    super();

    if (!$R.classes[PLACEHOLDER_PLUGIN_TOKEN]) {
      $R.add('plugin', PLACEHOLDER_PLUGIN_TOKEN, new RtRedactorPlaceholder());
    }
  }

  public canInstall(config: RedactorConfig): boolean {
    return (
      Array.isArray(config.plugins) &&
      Array.isArray(config.rtPlaceholders) &&
      config.plugins.includes(PLACEHOLDER_PLUGIN_TOKEN)
    );
  }

  public install(config: RedactorConfig): RedactorConfig {
    this.enable();

    return {
      ...config,
      plugins: mergeRedactorConfigArrField(config, 'plugins', [
        'variable',
        ANGULAR_ADAPTER_PLUGIN_TOKEN,
      ]),
    };
  }
}
